import { IconButton, ListSubheader, makeStyles, TextField, Tooltip, useMediaQuery, useTheme, TextFieldProps } from '@material-ui/core';
import { Delete, Edit, Search, Event, Flag, FlagOutlined } from '@material-ui/icons';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import clsx from 'clsx';
import { useIsMobile } from '../hooks/useIsMobile';

const useStyles = makeStyles((theme) => ({
  listboxComponent: {},
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid black',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItem: {
    width: '100%',
    display: 'inline-flex',
  },
  listItemStatus: {
    padding: theme.spacing(1),
    paddingRight: 0,
    paddingLeft: theme.spacing(2),
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '20% 30% 10% 40%',
  },
  listItemLabel: {
    padding: '4px 0',
  },
  listItemField: {
    margin: 'auto',
    marginRight: 2,
    textAlign: 'left',
  },
  listIcon: {
    margin: 'auto',
    marginRight: 0,
    color: theme.palette.grey[500],
  },
  searchTextField: {
    width: '100%',
  },
  dontRotate: {
    transform: 'none',
    color: theme.palette.primary.main,
  },
  listbox: {
    minHeight: '200px',
    overflowX: 'hidden !important' as 'hidden',
  },
  listItemText: {
    display: 'inline-flex',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listItemTitle: {
    margin: 'auto',
    marginLeft: 0,
    fontWeight: 'bold',
    display: 'flex',
    whiteSpace: 'nowrap',
    fontSize: '1em',
  },
  listItemContent: {
    margin: 'auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    paddingLeft: '10px',
    textAlign: 'left',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '.8em',
    },
  },
  listItemActions: {
    display: 'inline-flex',
    fontSize: '1.25em',
    marginLeft: 'auto',
    flex: '0 0 auto',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1em',
    },
  },
  listItemAction: {
    fontSize: 'inherit',
  },
  stacked: {
    flexDirection: 'column',
  },
}));

export interface IGenericAutocomplete<T, Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, FreeSolo extends boolean | undefined>
  extends Partial<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> {
  label?: string;
  filterFields?: string[];
  idField?: string | ((option: T, value: T) => boolean);
  labelField?: string | ((option: T) => string);
  titleField?: string;
  contentField?: string;
  deletable?: boolean;
  editable?: boolean;
  onDelete?: (option: T) => void;
  onEdit?: (option: T) => void;
  variant?: 'filled' | 'outlined' | 'standard' | undefined;
  suggestNew?: boolean;
  getNewSuggestion?: (inputVal: string) => T;
  showNewSuggestion?: 'top' | 'bottom';
  onlyShowSuggestionIfEmpty?: boolean;
  newSuggestionText?: (text: string) => string;
  inputRef?: any;
  minWidth?: number;
  stacked?: boolean;
  multiple?: Multiple;
  freeSolo?: FreeSolo;
  disableClearable?: DisableClearable;
  noContent?: boolean;
  placeholder?: string;
  TextFieldProps?: TextFieldProps;
}

export function GenericAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  label,
  filterFields,
  options,
  idField = 'id',
  labelField,
  titleField: _titleField,
  contentField = 'description',
  deletable = false,
  editable = false,
  onDelete,
  onEdit,
  variant = 'outlined',
  suggestNew = false,
  getNewSuggestion,
  showNewSuggestion = 'bottom',
  newSuggestionText = (text) => `Add "${text}"`,
  inputRef,
  onlyShowSuggestionIfEmpty = false,
  minWidth,
  stacked = false,
  noContent = false,
  placeholder,
  TextFieldProps,
  ...props
}: IGenericAutocomplete<T, Multiple, DisableClearable, FreeSolo>) {
  const classes = useStyles();
  const isMobile = useIsMobile();

  stacked = stacked || isMobile;
  labelField = labelField ?? _titleField ?? 'name';

  let titleField: string = _titleField ? _titleField : typeof labelField === 'string' ? labelField : 'name';

  const handleDelete = (option) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(option);
  };

  const handleEdit = (option) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit?.(option);
  };

  return (
    <Autocomplete
      filterOptions={(options: T[], state: any) => {
        const inputValue = state.inputValue === '' ? props?.inputValue : state.inputValue;
        const value = inputValue?.toLowerCase();
        const empty = !inputValue || inputValue === '';

        if (empty) return options ?? [];

        const fields = filterFields ?? [idField, titleField, contentField];
        if (typeof labelField === 'string' && titleField !== labelField) fields.push(labelField);

        const filtered =
          options?.filter((option) => {
            return fields.filter((field) => (typeof field === 'string' ? option?.[field] : field(option, value))?.toLowerCase?.()?.indexOf(value) > -1)?.length > 0;
          }) ?? [];

        // Suggest the creation of a new value
        if (getNewSuggestion && value !== '' && (!onlyShowSuggestionIfEmpty || filtered.length === 0)) {
          let sugg = getNewSuggestion(inputValue);
          sugg['__isNewSuggestion'] = true;
          if (showNewSuggestion === 'top') filtered.unshift(sugg);
          else filtered.push(sugg);
        }

        return filtered;
      }}
      getOptionLabel={typeof labelField === 'string' ? (option: any) => option?.[labelField as string] ?? '' : labelField}
      getOptionSelected={typeof idField === 'string' ? (option: T, value: T) => option?.[idField] === value?.[idField] : idField}
      ListboxComponent={ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      renderOption={(option: any) => {
        if (!option) return null;
        return (
          <Tooltip
            title={option[contentField] ?? ''}
            classes={{ tooltip: classes.tooltip }}
            enterDelay={500}
            enterNextDelay={500}
            disableHoverListener={Boolean(!option[contentField] && option['__isNewSuggestion'])}
          >
            <div style={{ minWidth: minWidth ? minWidth + 'px' : undefined }} className={classes.listItem}>
              {!option['__isNewSuggestion'] ? (
                <>
                  <div className={clsx(classes.listItemText, stacked ? classes.stacked : '')}>
                    <div style={{ width: noContent ? '100%' : '' }} className={classes.listItemTitle}>
                      {option[titleField]}
                    </div>
                    {noContent ? null : (
                      <div style={{ paddingLeft: stacked ? 0 : undefined }} className={classes.listItemContent}>
                        {option[contentField]}
                      </div>
                    )}
                  </div>
                  {editable || deletable || option?.['isSchedule'] || option?.['isFlagged'] ? (
                    <div className={clsx(classes.listItemActions, stacked ? classes.stacked : '')}>
                      {option?.['isFlagged'] !== undefined ? (
                        option?.['isFlagged'] ? (
                          <Flag fontSize='inherit' style={{ color: 'red' }} />
                        ) : (
                          <FlagOutlined fontSize='inherit' />
                        )
                      ) : null}
                      {option?.['isSchedule'] ? (
                        <div className={classes.listItemAction}>
                          <Event fontSize='inherit' />
                        </div>
                      ) : null}
                      {editable ? (
                        <IconButton className={classes.listItemAction} onClick={handleEdit(option)} size='small'>
                          <Edit fontSize='inherit' />
                        </IconButton>
                      ) : null}
                      {deletable ? (
                        <IconButton className={classes.listItemAction} onClick={handleDelete(option)} size='small'>
                          <Delete fontSize='inherit' />
                        </IconButton>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                newSuggestionText(option[titleField])
              )}
            </div>
          </Tooltip>
        );
      }}
      renderInput={(params) => {
        const refFunc = (e) => {
          params.inputProps['ref'].current = e;
          if (inputRef) inputRef.current = e;
        };
        return (
          <TextField
            {...(TextFieldProps ?? {})}
            {...params}
            inputProps={{ ...params.inputProps, ref: refFunc }}
            className={classes.searchTextField}
            label={label}
            variant={variant}
            placeholder={placeholder}
          />
        );
      }}
      autoComplete
      fullWidth
      classes={{ popupIndicatorOpen: classes.dontRotate, listbox: classes.listbox }}
      popupIcon={<Search />}
      options={options ?? []}
      limitTags={2}
      ListboxProps={{ rows: 1 + (stacked ? 1 : 0) }}
      {...props}
    />
  );
}

//https://material-ui.com/components/autocomplete/#virtualization

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
  const { children, ...other } = props as any;
  const rows = other?.rows ?? 1;
  const classes = useStyles();
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = rows * (smUp ? 30 : 36);

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref} className={classes.listboxComponent}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
